// src/app/pages/admin/AdminDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet,
  Users,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  FileText,
  User as UserIcon,
  RefreshCcw,
  Filter,
  Search,
  MessageSquare,
  Clock,
  TrendingUp,
  Shield,
} from "lucide-react";

import { WebhookMonitor } from "../../components/admin/WebhookMonitor";
import { TreasuryBalanceWidget } from "../../components/admin/TreasuryBalanceWidget";

import {
  adminListKycSubmissions,
  adminApproveKyc,
  adminRejectKyc,
  getAdminKycFileUrl,
} from "../../services/adminKyc.js";

function StatusPill({ status }) {
  const s = (status || "").toUpperCase();
  const base =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  if (s === "PENDING") return <span className={`${base} bg-amber-500/10 text-amber-300 border-amber-500/30`}>PENDING</span>;
  if (s === "VERIFIED") return <span className={`${base} bg-emerald-500/10 text-emerald-300 border-emerald-500/30`}>VERIFIED</span>;
  if (s === "REJECTED") return <span className={`${base} bg-red-500/10 text-red-300 border-red-500/30`}>REJECTED</span>;
  return <span className={`${base} bg-slate-500/10 text-slate-200 border-slate-500/30`}>UNVERIFIED</span>;
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "—";
  }
}

export function AdminDashboard() {
  // KYC state
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState("");
  const [kycItems, setKycItems] = useState([]);

  // filters
  const [status, setStatus] = useState("PENDING");
  const [email, setEmail] = useState("");

  // modal-ish lightweight confirm
  const [busyUserId, setBusyUserId] = useState(null);

  const canAct = (row) => (row?.kyc_status || "").toUpperCase() === "PENDING";
  const hasDocs = (row) => !!row?.id_document || !!row?.selfie;

  async function loadKyc() {
    setKycLoading(true);
    setKycError("");
    try {
      const data = await adminListKycSubmissions({
        status,
        email: email || undefined,
        limit: 50,
        offset: 0,
      });
      setKycItems(data?.items || []);
    } catch (e) {
      setKycError(e?.response?.data?.detail || e?.message || "Failed to load KYC submissions");
      setKycItems([]);
    } finally {
      setKycLoading(false);
    }
  }

  useEffect(() => {
    loadKyc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = useMemo(() => {
    const q = (email || "").trim().toLowerCase();
    if (!q) return kycItems;
    return (kycItems || []).filter((r) => String(r.email || "").toLowerCase().includes(q));
  }, [kycItems, email]);

  async function onApprove(row) {
    if (!row?.user_id) return;
    const ok = window.confirm(`Approve KYC for ${row.email}?`);
    if (!ok) return;

    setBusyUserId(row.user_id);
    try {
      await adminApproveKyc(row.user_id, null);
      await loadKyc();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Approve failed");
    } finally {
      setBusyUserId(null);
    }
  }

  async function onReject(row) {
    if (!row?.user_id) return;
    const reason = window.prompt(`Reject KYC for ${row.email}.\nProvide a reason (required):`);
    if (!reason || reason.trim().length < 3) {
      alert("Rejection reason must be at least 3 characters.");
      return;
    }

    setBusyUserId(row.user_id);
    try {
      await adminRejectKyc(row.user_id, reason.trim());
      await loadKyc();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Reject failed");
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6 text-white">Admin Dashboard</h1>

      {/* Quick Action Cards - Top Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* ✅ NEW: Trade Management */}
        <Link
          to="/admin/trade-management"
          className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 rounded-xl border border-yellow-800/40 p-4 hover:border-yellow-700/60 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-yellow-600/20 flex items-center justify-center group-hover:bg-yellow-600/30 transition">
              <Clock size={20} className="text-yellow-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Trade Management</div>
              <div className="text-xs text-yellow-200/60">Review & settle trades</div>
            </div>
          </div>
        </Link>

        {/* Treasury */}
        <Link
          to="/admin/treasury"
          className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl border border-indigo-800/40 p-4 hover:border-indigo-700/60 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-600/20 flex items-center justify-center group-hover:bg-indigo-600/30 transition">
              <Wallet size={20} className="text-indigo-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Treasury</div>
              <div className="text-xs text-indigo-200/60">Manage funds & withdrawals</div>
            </div>
          </div>
        </Link>

        {/* User Balances */}
        <Link
          to="/admin/users/balances"
          className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-xl border border-cyan-800/40 p-4 hover:border-cyan-700/60 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-600/20 flex items-center justify-center group-hover:bg-cyan-600/30 transition">
              <Users size={20} className="text-cyan-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">User Balances</div>
              <div className="text-xs text-cyan-200/60">View & adjust balances</div>
            </div>
          </div>
        </Link>

        {/* Admin Chat */}
        <Link
          to="/admin/chat"
          className="bg-gradient-to-br from-cyan-900/30 to-teal-900/30 rounded-xl border border-cyan-800/40 p-4 hover:border-cyan-700/60 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-600/20 flex items-center justify-center group-hover:bg-cyan-600/30 transition">
              <MessageSquare size={20} className="text-cyan-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Support Inbox</div>
              <div className="text-xs text-cyan-200/60">Chat with users</div>
            </div>
          </div>
        </Link>
      </div>

      {/* Second Row - Stats Overview (Optional) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Treasury Overview */}
        <TreasuryBalanceWidget />

        {/* Quick Stats Widget */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-800/40 grid place-items-center text-white">
              <TrendingUp size={20} />
            </div>
            <h3 className="text-lg font-semibold text-white">Quick Stats</h3>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Total Users</span>
              <span className="text-white font-semibold">—</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Pending Trades</span>
              <span className="text-yellow-300 font-semibold">—</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Today's Volume</span>
              <span className="text-white font-semibold">—</span>
            </div>
          </div>

          <Link
            to="/admin/trade-management?tab=all"
            className="mt-4 block w-full py-2 text-center rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
          >
            View All Trades
          </Link>
        </div>

        {/* Withdrawals Widget */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-amber-800/40 grid place-items-center text-white">
              <ArrowUpFromLine size={20} />
            </div>
            <h3 className="text-lg font-semibold text-white">Withdrawals</h3>
          </div>

          <div className="text-sm text-gray-400">
            Review and process pending withdrawal requests.
          </div>

          <Link
            to="/admin/treasury?tab=withdrawals"
            className="mt-4 block w-full py-2 text-center rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm"
          >
            Review Withdrawals
          </Link>
        </div>
      </div>

      {/* KYC Review Panel */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-xl font-bold text-white">KYC Review</h2>
            <p className="text-sm text-gray-400">
              Review pending identity submissions and approve/reject.
            </p>
          </div>

          <button
            onClick={loadKyc}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
            disabled={kycLoading}
            type="button"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 text-gray-300 text-sm">
            <Filter size={16} />
            <span className="text-gray-400">Status</span>
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="md:w-56 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-100 text-sm"
          >
            <option value="PENDING">PENDING (Actionable)</option>
            <option value="UNVERIFIED">UNVERIFIED</option>
            <option value="VERIFIED">VERIFIED</option>
            <option value="REJECTED">REJECTED</option>
          </select>

          <div className="flex-1" />

          <div className="relative md:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Search email…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-100 text-sm"
            />
          </div>
        </div>

        {kycError ? (
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
            {kycError}
          </div>
        ) : null}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-950/40 text-gray-300">
              <tr className="text-left">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Documents</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-800">
              {kycLoading ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    No results.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => {
                  const isBusy = busyUserId === row.user_id;

                  return (
                    <tr key={row.user_id} className="hover:bg-gray-950/20">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-white">{row.email}</div>
                        <div className="text-xs text-gray-500">{row.user_id}</div>
                      </td>

                      <td className="px-4 py-4">
                        <StatusPill status={row.kyc_status} />
                      </td>

                      <td className="px-4 py-4 text-gray-300">
                        {fmtDate(row.kyc_submitted_at)}
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <a
                            className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
                              row.id_document
                                ? "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
                                : "bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed"
                            }`}
                            href={row.id_document ? getAdminKycFileUrl(row.id_document) : undefined}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => {
                              if (!row.id_document) e.preventDefault();
                            }}
                          >
                            <FileText size={14} />
                            ID
                          </a>

                          <a
                            className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
                              row.selfie
                                ? "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
                                : "bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed"
                            }`}
                            href={row.selfie ? getAdminKycFileUrl(row.selfie) : undefined}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => {
                              if (!row.selfie) e.preventDefault();
                            }}
                          >
                            <UserIcon size={14} />
                            Selfie
                          </a>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canAct(row) ? (
                            <>
                              <button
                                onClick={() => onApprove(row)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-200 text-xs"
                                type="button"
                              >
                                <CheckCircle2 size={16} />
                                Approve
                              </button>
                              <button
                                onClick={() => onReject(row)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-200 text-xs"
                                type="button"
                              >
                                <XCircle size={16} />
                                Reject
                              </button>
                            </>
                          ) : hasDocs(row) ? (
                            <span className="text-xs text-gray-500">Awaiting user submission</span>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Approve/Reject actions are enabled only for <span className="text-gray-300">PENDING</span> submissions.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WebhookMonitor />

        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl border border-indigo-700 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur-sm grid place-items-center text-white">
              <Shield size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Admin Tools</h3>
              <p className="text-indigo-200">Manage users, trades, and system settings</p>
            </div>
          </div>

          <p className="text-indigo-200 mb-6">
            Access all admin tools including user management, trade review, treasury operations, and system settings from one place.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/admin/users"
              className="py-3 text-center rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium"
            >
              <Users size={16} className="inline mr-2" />
              Manage Users
            </Link>

            <Link
              to="/admin/settings"
              className="py-3 text-center rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium"
            >
              <Shield size={16} className="inline mr-2" />
              System Settings
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
